<template>
  <v-card height="650px" class="route-waypoints-card">
    <v-card-title>
      <v-row no-gutters>
        <v-col :cols="4">
          <ThemedTextField
            label="Longitude"
            outlined
            persistent-placeholder
            v-model="wayPointX"
            type="number"
            class="mr-2"
            :step="0.5"
            dense
          />
        </v-col>
        <v-col :cols="4">
          <ThemedTextField
            label="Latitude"
            outlined
            persistent-placeholder
            v-model="wayPointY"
            type="number"
            class="mr-2"
            :step="0.5"
            dense
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="addWayPoint(wayPointX, wayPointY)"
            >
              <v-icon color="var(--color-secondary)"
                >mdi-plus-circle-outline</v-icon
              >
            </v-btn>
          </template>
          <span>Add a waypoint</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="onDeleteAllPressed()">
              <v-icon color="var(--color-secondary)"> mdi-delete-sweep</v-icon>
            </v-btn>
          </template>
          <span>Delete all waypoints</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon @click="handleFileImport">
              <v-icon color="var(--color-secondary)"> mdi-cloud-upload</v-icon>
              <input
                ref="uploader"
                class="d-none"
                type="file"
                @change="onFileChanged"
              />
            </v-btn>
          </template>
          <span>Import RTZ</span>
        </v-tooltip>
      </v-row>
    </v-card-title>
    <v-card-text class="mt-4 overflow-y-auto vuebar-element">
      <EmptyRoute v-if="waypointListIsEmpty" />
      <v-timeline v-else dense align-top class="ml-n7">
        <v-timeline-item
          v-for="(point, index) in waypoints"
          :icon="determineWaypointIcon(index)"
          color="var(--color-secondary)"
          :key="index"
        >
          <v-row no-gutters class="mt-1">
            <v-col :cols="5">
              <ThemedTextField
                :label="setWayPointLabel(index, true)"
                outlined
                persistent-placeholder
                v-model="point[0]"
                type="number"
                class="mr-2"
                :step="0.1"
              />
            </v-col>
            <v-col :cols="5">
              <ThemedTextField
                :label="setWayPointLabel(index, false)"
                outlined
                persistent-placeholder
                v-model="point[1]"
                type="number"
                class="mr-2"
                :step="0.1"
              />
            </v-col>
            <v-btn icon @click="onDeleteIconPressed(index)">
              <v-icon color="var(--color-secondary)"> mdi-trash-can</v-icon>
            </v-btn>
          </v-row>
        </v-timeline-item>
      </v-timeline>
    </v-card-text>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>
  
<script>
import { mapGetters, mapState } from "vuex";
import EmptyRoute from "./EmptyRoute.vue";
import ThemedTextField from "@/core/ThemedTextField.vue";
export default {
  name: "SetRouteWaypointComponent",
  components: { EmptyRoute, ThemedTextField },
  data: function () {
    return {
      waypoints: [],
      wayPointX: null,
      wayPointY: null,
      fileContent: null,
      snackbar: {
        show: false,
        text: "",
        color: "error",
      },
    };
  },
  computed: {
    ...mapGetters("weatherRouting", ["getPortLocations"]),
    ...mapState("weatherRouting/createRoute", ["currentWayPoints"]),
    allPortLocations() {
      return this.getPortLocations != null ? this.getPortLocations : [];
    },
    waypointListIsEmpty() {
      return this.waypoints.length == 0;
    },
  },
  methods: {
    addWayPoint(x, y) {
      this.waypoints.push([Number(x), Number(y)]);
    },
    setWayPointLabel(index, isLon) {
      return isLon ? `WP${index}-Lon` : `WP${index}-Lat`;
    },
    determineWaypointIcon(index) {
      if (index == 0) {
        return "mdi-flag";
      } else if (index == this.waypoints.length - 1) {
        return "mdi-flag-checkered";
      } else {
        return "mdi-star-four-points";
      }
    },
    onDeleteIconPressed(index) {
      this.waypoints.splice(index, 1);
    },
    onDeleteAllPressed() {
      this.waypoints.splice(0, this.waypoints.length);
      this.$refs.uploader.value = "";
    },
    handleFileImport() {
      this.isSelecting = true;

      // After obtaining the focus when closing the FilePicker, return the button state to normal
      window.addEventListener(
        "focus",
        () => {
          this.isSelecting = false;
        },
        { once: true }
      );

      // Trigger click on the FileInput
      this.$refs.uploader.click();
    },
    onFileChanged(e) {
      const file = e.target.files[0]; // Get the selected file
      if (file) {
        this.file = file;
        this.readFile(file); // Read the content of the file
      }
    },
    readFile(file) {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          this.fileContent = event.target.result;
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(this.fileContent, "text/xml");

          const rtzElement = xmlDoc.documentElement;
          const namespace = rtzElement.namespaceURI;

          const waypoints = xmlDoc.getElementsByTagNameNS(
            namespace,
            "waypoint"
          );

          if (waypoints.length === 0) {
            this.showError(
              "No waypoints found in the imported file - Check rtz version"
            );
            return;
          }

          // Clear existing waypoints before adding new ones
          this.waypoints = [];

          for (let i = 0; i < waypoints.length; i++) {
            const waypoint = waypoints[i];
            const position = waypoint.getElementsByTagNameNS(
              namespace,
              "position"
            )[0];
            const lat = position.getAttribute("lat");
            const lon = position.getAttribute("lon");

            this.waypoints.push([Number(lon), Number(lat)]);
          }
        } catch (error) {
          this.showError(
            "Error processing the file. Please check the file format."
          );
        }
      };
      reader.onerror = () => {
        this.showError("Error reading the file");
      };

      reader.readAsText(file);
    },
    showError(message) {
      this.snackbar = {
        show: true,
        text: message,
        color: "error",
      };
    },
  },
  watch: {
    waypoints(newPoints) {
      this.$store.commit(
        "weatherRouting/createRouteModule/SET_CURRENT_WAYPOINTS",
        newPoints,
        { root: true }
      );
    },
  },
};
</script>
  
<style scoped>
.v-timeline::before {
  top: 55px;
  height: calc(100% - 110px);
}
.vuebar-element {
  height: 92%;
}
.route-waypoints-card {
  background-color: var(--color-surface) !important;
}
</style>
  
  